import { inject, Injectable, DOCUMENT } from "@angular/core";
import { AStorageService } from "./storage.abstract";
import { UrlParts } from "@uirouter/angular";
import { fromEvent, map } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Injectable({ providedIn: "root" })
export class StorageSessionService extends AStorageService {

  private readonly _warningProdBannerActiveKey = 'warningProdBannerActive';
  private readonly _lastUrlKey = 'airtrfx_cms_last_url';
  private readonly _lastUrlPartsKey = 'airtrfx_cms_last_url_parts';
  private readonly _apiKeyKey = 'api_key';

  private _window = inject(DOCUMENT).defaultView;
  protected readonly _internalObserver = fromEvent<StorageEvent>(this._window, "storage").pipe(takeUntilDestroyed());
  protected readonly _storage = sessionStorage;

  public override readonly get = Object.assign(
    super.get,
    {
      lastUrl: () => this.get<string>(this._lastUrlKey),
      lastUrlParts: () => this.get<UrlParts>(this._lastUrlPartsKey),
      prodWarning: () => this.get<boolean>(this._warningProdBannerActiveKey),
      apiKey: () => {
        const value = this.get<string>(this._apiKeyKey);
        return value ? window.atob(value) : null;
      }
    }
  );

  public override readonly set = Object.assign(
    super.set,
    {
      lastUrl: (value: string) => this.set(this._lastUrlKey, value),
      lastUrlParts: (value: UrlParts) => this.set(this._lastUrlPartsKey, value),
      prodWarning: (value: boolean) => this.set(this._warningProdBannerActiveKey, value),
      apiKey: (value: string) => {
        const final = value ? window.btoa(value) : value;
        this.set(this._apiKeyKey, final);
      }
    }
  );

  public override readonly remove = Object.assign(
    super.remove,
    {
      apiKey: () => this.remove(this._apiKeyKey),
      lastUrl: () => this.remove(this._lastUrlKey),
      lastUrlParts: () => this.remove(this._lastUrlPartsKey),
      prodWarning: () => this.remove(this._warningProdBannerActiveKey)
    }
  );

  public override readonly observe = Object.assign(
    super.observe,
    {
      lastUrl: () => this.observe<string>(this._lastUrlKey),
      lastUrlParts: () => this.observe<UrlParts>(this._lastUrlPartsKey),
      prodWarning: () => this.observe<boolean>(this._warningProdBannerActiveKey),
      apiKey: () => this.observe<string>(this._apiKeyKey).pipe(map(value => value ? window.atob(value) : null))
    }
  );
}
